import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";

import { CONTACT_EMAIL } from "../data/constants";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useUserInfo } from "../hooks/useUserInfo";
import { Theme } from "../theme";

// アニメーション
const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

// Styled Components
const PageContainer = styled.div<{ theme: Theme }>`
	min-height: 100vh;
	background: ${props => props.theme.colors.background.primary};
	transition: background-color 0.3s ease;
`;

const Container = styled.div`
	max-width: 900px;
	margin: 0 auto;
	padding: 3rem 2rem 4rem 2rem;

	@media (max-width: 768px) {
		padding: 2rem 1rem 3rem 1rem;
	}
`;

const Header = styled.div`
	margin-bottom: 3rem;
	animation: ${fadeInUp} 0.6s ease-out;
`;

const Title = styled.h1<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.pageHeading.fontSize};
	font-weight: ${props => props.theme.typography.roles.pageHeading.fontWeight};
	line-height: ${props => props.theme.typography.roles.pageHeading.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.pageHeading.letterSpacing};
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 0.5rem;

	@media (max-width: 768px) {
		letter-spacing: -0.015em;
	}
`;

const LastUpdated = styled.p<{ theme: Theme }>`
	font-size: 0.95rem;
	color: ${props => props.theme.colors.text.tertiary};
	margin-top: 1rem;
`;

const Content = styled.div`
	animation: ${fadeInUp} 0.6s ease-out 0.1s both;
`;

const TableContainer = styled.div<{ theme: Theme }>`
	overflow-x: auto;
	border-radius: 12px;
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	margin-bottom: 3rem;
	background: ${props => props.theme.colors.background.secondary};
	box-shadow: ${props => props.theme.mode === 'light'
		? '0 2px 8px rgba(0, 0, 0, 0.05)'
		: '0 2px 8px rgba(0, 0, 0, 0.2)'};
`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
`;

const TableRow = styled.tr<{ theme: Theme }>`
	border-bottom: 1px solid ${props => props.theme.colors.neutral[200]};

	&:last-child {
		border-bottom: none;
	}
`;

const TableHeader = styled.th<{ theme: Theme }>`
	padding: 1.25rem 1.5rem;
	text-align: left;
	font-weight: 700;
	font-size: 0.95rem;
	color: ${props => props.theme.colors.text.primary};
	background: ${props => props.theme.mode === 'light'
		? props.theme.colors.neutral[100]
		: props.theme.colors.neutral[200]};
	vertical-align: top;
	min-width: 160px;
	width: 30%;

	@media (max-width: 768px) {
		padding: 1rem;
		font-size: 0.875rem;
		min-width: 120px;
		display: block;
		width: 100%;
		border-bottom: none;
	}
`;

const TableData = styled.td<{ theme: Theme }>`
	padding: 1.25rem 1.5rem;
	color: ${props => props.theme.colors.text.secondary};
	font-size: 0.95rem;
	line-height: 1.8;
	white-space: pre-line;

	@media (max-width: 768px) {
		padding: 0 1rem 1rem 1rem;
		font-size: 0.875rem;
		display: block;
		width: 100%;
	}
`;

const Section = styled.section`
	margin-bottom: 3rem;
`;

const SectionTitle = styled.h2<{ theme: Theme }>`
	font-size: 1.75rem;
	font-weight: 700;
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid ${props => props.theme.colors.primary.main};

	@media (max-width: 768px) {
		font-size: 1.5rem;
	}
`;

const NotesList = styled.ul<{ theme: Theme }>`
	list-style: none;
	padding: 0;
	margin: 0;
`;

const NotesItem = styled.li<{ theme: Theme }>`
	padding: 0.75rem 0;
	padding-left: 1.5rem;
	position: relative;
	color: ${props => props.theme.colors.text.secondary};
	line-height: 1.8;

	&::before {
		content: '•';
		position: absolute;
		left: 0;
		color: ${props => props.theme.colors.primary.main};
		font-weight: bold;
		font-size: 1.25rem;
	}
`;

const ContactBox = styled.div<{ theme: Theme }>`
	padding: 2rem;
	border-radius: 12px;
	background: ${props => props.theme.mode === 'light'
		? props.theme.colors.neutral[100]
		: props.theme.colors.neutral[200]};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	text-align: center;
`;

const ContactDescription = styled.p<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.secondary};
	margin-bottom: 1.5rem;
	line-height: 1.8;
`;

const EmailLink = styled.a<{ theme: Theme }>`
	display: inline-block;
	color: ${props => props.theme.colors.primary.main};
	font-weight: 600;
	font-size: 1.125rem;
	text-decoration: none;
	padding: 1rem 2rem;
	border-radius: 8px;
	background: ${props => props.theme.mode === 'light'
		? 'rgba(59, 130, 246, 0.1)'
		: 'rgba(59, 130, 246, 0.2)'};
	transition: all 0.3s ease;
	margin-bottom: 1rem;

	&:hover {
		background: ${props => props.theme.mode === 'light'
			? 'rgba(59, 130, 246, 0.2)'
			: 'rgba(59, 130, 246, 0.3)'};
		transform: translateY(-2px);
	}

	@media (max-width: 768px) {
		font-size: 1rem;
		padding: 0.875rem 1.5rem;
	}
`;

const ResponseNote = styled.p<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.tertiary};
	font-size: 0.875rem;
	margin-top: 1rem;
`;

const FooterWrapper = styled.div`
	margin-top: 6rem;
`;

const CommercialLaw = () => {
	const { theme } = useTheme();
	const { t } = useLanguage();
	const userInfo = useUserInfo();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const lastUpdatedDate = "2025年1月14日";

	return (
		<React.Fragment>
			<Helmet>
				<title>{`${t.commercialLaw.title} | ${userInfo.main.title}`}</title>
				<meta name="description" content={t.commercialLaw.title} />
			</Helmet>

			<PageContainer theme={theme}>
				<NavBar />
				<Container>
					<Header>
						<Title theme={theme}>{t.commercialLaw.title}</Title>
						<LastUpdated theme={theme}>
							{t.commercialLaw.lastUpdated}: {lastUpdatedDate}
						</LastUpdated>
					</Header>

					<Content>
						<TableContainer theme={theme}>
							<Table>
								<tbody>
									<TableRow theme={theme}>
										<TableHeader theme={theme}>{t.commercialLaw.table.seller}</TableHeader>
										<TableData theme={theme}>{t.commercialLaw.values.sellerName}</TableData>
									</TableRow>
									<TableRow theme={theme}>
										<TableHeader theme={theme}>{t.commercialLaw.table.operator}</TableHeader>
										<TableData theme={theme}>{t.commercialLaw.values.operatorName}</TableData>
									</TableRow>
									<TableRow theme={theme}>
										<TableHeader theme={theme}>{t.commercialLaw.table.address}</TableHeader>
										<TableData theme={theme}>{t.commercialLaw.values.addressDisclosure}</TableData>
									</TableRow>
									<TableRow theme={theme}>
										<TableHeader theme={theme}>{t.commercialLaw.table.contact}</TableHeader>
										<TableData theme={theme}>
											{t.commercialLaw.values.contactEmailLabel}: {CONTACT_EMAIL}
											{"\n"}
											{t.commercialLaw.values.contactNote}
										</TableData>
									</TableRow>
									<TableRow theme={theme}>
										<TableHeader theme={theme}>{t.commercialLaw.table.price}</TableHeader>
										<TableData theme={theme}>{t.commercialLaw.values.priceNote}</TableData>
									</TableRow>
									<TableRow theme={theme}>
										<TableHeader theme={theme}>{t.commercialLaw.table.additionalFees}</TableHeader>
										<TableData theme={theme}>{t.commercialLaw.values.additionalFeesNote}</TableData>
									</TableRow>
									<TableRow theme={theme}>
										<TableHeader theme={theme}>{t.commercialLaw.table.paymentMethod}</TableHeader>
										<TableData theme={theme}>{t.commercialLaw.values.paymentMethodNote}</TableData>
									</TableRow>
									<TableRow theme={theme}>
										<TableHeader theme={theme}>{t.commercialLaw.table.paymentTiming}</TableHeader>
										<TableData theme={theme}>{t.commercialLaw.values.paymentTimingNote}</TableData>
									</TableRow>
									<TableRow theme={theme}>
										<TableHeader theme={theme}>{t.commercialLaw.table.deliveryTiming}</TableHeader>
										<TableData theme={theme}>{t.commercialLaw.values.deliveryTimingNote}</TableData>
									</TableRow>
									<TableRow theme={theme}>
										<TableHeader theme={theme}>{t.commercialLaw.table.returnPolicy}</TableHeader>
										<TableData theme={theme}>{t.commercialLaw.values.returnPolicyNote}</TableData>
									</TableRow>
									<TableRow theme={theme}>
										<TableHeader theme={theme}>{t.commercialLaw.table.serviceValidity}</TableHeader>
										<TableData theme={theme}>{t.commercialLaw.values.serviceValidityNote}</TableData>
									</TableRow>
									<TableRow theme={theme}>
										<TableHeader theme={theme}>{t.commercialLaw.table.requirements}</TableHeader>
										<TableData theme={theme}>{t.commercialLaw.values.requirementsNote}</TableData>
									</TableRow>
								</tbody>
							</Table>
						</TableContainer>

						<Section>
							<SectionTitle theme={theme}>{t.commercialLaw.notes.title}</SectionTitle>
							<NotesList theme={theme}>
								{t.commercialLaw.notes.items.map((item, index) => (
									<NotesItem key={index} theme={theme}>
										{item}
									</NotesItem>
								))}
							</NotesList>
						</Section>

						<Section>
							<SectionTitle theme={theme}>{t.commercialLaw.contactSection.title}</SectionTitle>
							<ContactBox theme={theme}>
								<ContactDescription theme={theme}>
									{t.commercialLaw.contactSection.description}
								</ContactDescription>
								<EmailLink
									href={`mailto:${CONTACT_EMAIL}`}
									theme={theme}
								>
									{CONTACT_EMAIL}
								</EmailLink>
								<ResponseNote theme={theme}>
									{t.commercialLaw.contactSection.responseNote}
								</ResponseNote>
							</ContactBox>
						</Section>
					</Content>

					<FooterWrapper>
						<Footer />
					</FooterWrapper>
				</Container>
			</PageContainer>
		</React.Fragment>
	);
};

export default CommercialLaw;
